'use client'

import type { RefObject } from 'react'
import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CirclePlay } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import { Button } from '@ui/web/components'
import { ManyPeopleHand } from '@ui/web/icon'

import { InClassNoticeDialog } from '../../../components'
import { useSharedTRPC } from '../../../context'
import { AssignModuleDialog } from '../../../lib/assign-module'
import { EditCourseDialog } from '../../../lib/edit-course'

type LearnProgressEntry = RouterOutputs['lesson']['getAllLearnProgress'][number]

export type ModuleDetails = RouterOutputs['lesson']['getLessonModuleDetails']

interface BottomNavProps {
  data: ModuleDetails
  userType: 'admin' | 'main' | 'child'
  type: 'web' | 'school'
  learnEnrollmentId?: string
  courseId?: string
  getLessonHref: (lessonIndex: number, searchParams?: Record<string, string | undefined>) => string
  learnProgressData: LearnProgressEntry[]
  onNavigate?: (href: string) => void
  ref: RefObject<HTMLDivElement | null>
  /** Called after self-enroll practice to get the back_to URL for the player */
  practicePlayerBackTo?: (enrollmentId: string) => string
}

type BottomAction =
  | { kind: 'join-challenge' }
  | { kind: 'start-learning' }
  | { kind: 'start-practice' }
  | { kind: 'assign' }
  | { kind: 'reassign' }
  | null

function resolveAction(props: {
  data: ModuleDetails
  userType: 'admin' | 'main' | 'child'
  learnEnrollmentId?: string
  courseId?: string
}): BottomAction {
  const { data, userType, learnEnrollmentId, courseId } = props

  if (userType === 'main' || userType === 'admin') {
    if (data.type === 'lessonModule' || data.type === 'course') {
      if (courseId) return { kind: 'reassign' }
      return { kind: 'assign' }
    }
    return null
  }

  if (data.type === 'challenge') {
    return { kind: 'join-challenge' }
  }

  if (learnEnrollmentId) {
    return { kind: 'start-learning' }
  }

  return { kind: 'start-practice' }
}

export function BottomNav({
  data,
  userType,
  learnEnrollmentId,
  courseId,
  getLessonHref,
  learnProgressData,
  onNavigate,
  ref,
  type,
  practicePlayerBackTo,
}: BottomNavProps) {
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()

  const [openAssignInitialAlert, setOpenAssignInitialAlert] = useState(false)
  const [openAssignBackModal, setOpenAssignBackModal] = useState(false)
  const [openInClassNotice, setOpenInClassNotice] = useState(false)

  const hasInClassLessons = data.type === 'lessonModule' && data.lessons.some((e) => e.lesson?.type === 'IN_CLASS')

  const { mutateAsync: createLearnProgress, isPending: isCreating } = useMutation({
    ...trpc.lesson.createLearnProgress.mutationOptions(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trpc.lesson.getAllLearnProgress.queryKey() })
    },
  })

  const { mutateAsync: editLearnEnrollment, isPending: isEditingEnrollment } = useMutation({
    ...trpc.lesson.editLearnEnrollment.mutationOptions(),
  })

  const { mutateAsync: selfEnrollChallenge, isPending: isEnrollingChallenge } = useMutation({
    ...trpc.lesson.selfEnrollChallenge.mutationOptions(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trpc.lesson.getActiveChallenges.queryKey() })
    },
  })

  const { mutateAsync: selfEnrollLessonModule, isPending: isEnrollingLessonModule } = useMutation({
    ...trpc.lesson.selfEnrollLessonModule.mutationOptions(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trpc.lesson.getAllLearnEnrollment.queryKey() })
    },
  })

  const action = resolveAction({ data, userType, learnEnrollmentId, courseId })

  const isCourseExpired = useMemo(() => {
    if (data.type !== 'course') return false
    if (!data.endDate) return false
    return new Date(data.endDate) < new Date()
  }, [data])

  const isProcessing = isCreating || isEditingEnrollment || isEnrollingChallenge || isEnrollingLessonModule

  if (!action) return null

  const handleStartLesson = async (enrollmentId?: string, playerBackTo?: string) => {
    const effectiveEnrollmentId = enrollmentId ?? learnEnrollmentId
    if (!effectiveEnrollmentId || isProcessing) return

    const extraParams: Record<string, string> = {}
    if (enrollmentId) extraParams.learnEnrollmentId = enrollmentId
    if (playerBackTo) extraParams.back_to = playerBackTo

    const isFirstTime = learnProgressData.length === 0

    if (isFirstTime) {
      await editLearnEnrollment({
        learnEnrollmentId: effectiveEnrollmentId,
        data: { startedAt: new Date(), status: 'IN_PROGRESS' },
      })
    }

    const existingInProgress = learnProgressData.find((lp) => lp.status === 'IN_PROGRESS')
    if (existingInProgress) {
      const lessonIndex = data.lessons.findIndex((entry) => entry.lesson?.id === existingInProgress.lessonId)
      if (lessonIndex !== -1) {
        const href = getLessonHref(lessonIndex + 1, { learnProgressId: existingInProgress.id, ...extraParams })
        onNavigate?.(href)
      }
      return // always return — do not create new progress if one exists
    }

    let lastCompletedIndex = -1
    for (let i = data.lessons.length - 1; i >= 0; i--) {
      const lesson = data.lessons[i]
      if (!lesson) continue
      if (!lesson.lesson) continue
      const progress = learnProgressData.find((lp) => lp.lessonId === lesson.lesson.id)
      if (progress?.status === 'COMPLETED') {
        lastCompletedIndex = i
        break
      }
    }

    const nextIndex = lastCompletedIndex + 1
    const nextLesson = data.lessons[nextIndex]
    if (!nextLesson?.lesson) return

    const [response] = await createLearnProgress({
      data: [
        {
          lessonId: nextLesson.lesson.id,
          learnEnrollmentId: effectiveEnrollmentId,
          startedAt: new Date(),
          status: 'IN_PROGRESS',
        },
      ],
    })

    if (!response) return

    const href = getLessonHref(nextIndex + 1, { learnProgressId: response.id, ...extraParams })
    onNavigate?.(href)
  }

  const handleJoinChallenge = async () => {
    if (isProcessing) return

    if (learnEnrollmentId) {
      await handleStartLesson()
      return
    }

    if (data.type !== 'challenge') return

    const { id: newEnrollmentId } = await selfEnrollChallenge({ challengeId: data.id })
    await handleStartLesson(newEnrollmentId)
  }

  const handleSelfEnrollPractice = async () => {
    if (isProcessing || data.type !== 'lessonModule') return

    const { id: newEnrollmentId } = await selfEnrollLessonModule({ lessonModuleId: data.id })
    const backTo = practicePlayerBackTo?.(newEnrollmentId)
    await handleStartLesson(newEnrollmentId, backTo)
  }

  return (
    <>
      <nav
        className='srd:w-full srd:h-fit srd:px-4 srd:py-3 srd:absolute srd:bottom-0 srd:left-0 srd:flex srd:items-center srd:justify-center srd:z-20'
        ref={ref}
      >
        {action.kind === 'start-learning' && (
          <Button
            size='lg'
            variant='gradient'
            onClick={() => void handleStartLesson()}
            disabled={isProcessing}
            loading={isProcessing}
          >
            <CirclePlay className='srd:w-5 srd:h-5 srd:mr-2' />
            {learnProgressData.length > 0 ? 'Lanjutkan' : 'Mulai belajar'}
          </Button>
        )}

        {action.kind === 'start-practice' && (
          <Button
            size='lg'
            variant='gradient'
            onClick={() => {
              if (hasInClassLessons) {
                setOpenInClassNotice(true)
              } else {
                void handleSelfEnrollPractice()
              }
            }}
            disabled={isProcessing}
            loading={isProcessing}
          >
            <CirclePlay className='srd:w-5 srd:h-5 srd:mr-2' />
            Mulai latihan
          </Button>
        )}

        {action.kind === 'join-challenge' && (
          <Button
            size='lg'
            variant='gradient'
            onClick={handleJoinChallenge}
            disabled={isProcessing}
            loading={isProcessing}
          >
            <CirclePlay className='srd:w-5 srd:h-5 srd:mr-2' />
            Ikut tantangan
          </Button>
        )}

        {action.kind === 'assign' && (
          <Button size='lg' variant='gradient' onClick={() => setOpenAssignInitialAlert(true)}>
            <ManyPeopleHand className='srd:w-5 srd:h-5 srd:mr-2' />
            Jadikan tugas
          </Button>
        )}

        {action.kind === 'reassign' && (
          <Button size='lg' variant='gradient' onClick={() => setOpenAssignBackModal(true)} disabled={isCourseExpired}>
            <ManyPeopleHand className='srd:w-5 srd:h-5 srd:mr-2' />
            Jadikan tugas lagi
          </Button>
        )}
      </nav>

      {action.kind === 'assign' && data.type === 'lessonModule' && (
        <AssignModuleDialog
          type={type}
          open={openAssignInitialAlert}
          onOpenChange={setOpenAssignInitialAlert}
          module={{
            id: data.id,
            title: data.title,
            coverUrl: data.coverUrl ?? '',
          }}
          onSuccess={({ courseId }) => {
            onNavigate?.(`/belajar/t/misi?type=course&id=${courseId}`)
          }}
          onAddChild={() => {
            onNavigate?.(type === 'school' ? '/pengaturan/kelola-anggota/murid' : '/pengaturan/orang-tua')
          }}
        />
      )}

      {action.kind === 'reassign' && data.type === 'course' && (
        <EditCourseDialog
          type={type}
          open={openAssignBackModal}
          onOpenChange={setOpenAssignBackModal}
          courseId={data.id}
          initialTitle={data.title}
          initialStartDate={data.startDate}
          initialEndDate={data.endDate}
          initialCreatedAt={data.createdAt}
          initialParticipantIds={data.learnEnrollments.map((e) => e.childId)}
        />
      )}

      <InClassNoticeDialog open={openInClassNotice} onOpenChange={setOpenInClassNotice} />
    </>
  )
}
